import * as $ from 'jquery';
import { disposableSubscription} from 'common/barrels/core';
import { Observable, Subject } from 'common/barrels/rx';
import { Map } from 'immutable';
import * as array from 'common/functions/array';
import { preserveScroll } from 'common/functions/scroll';

import { ContainerControl } from 'runtime/controls/containercontrol';
import { ControlArgs } from 'runtime/controls/controlargs';
import { registerBinding } from 'runtime/bindingengine';
import { applyBindings } from 'runtime/bindingengine';
import { ViewRequested } from 'runtime/events/viewrequested';
import { MethodRouter } from 'runtime/methodrouter';
import { RootContainer } from 'runtime/controls/rootcontainer';

import 'runtime/styles/controls/viewflow.less';

class ViewBinding {
    constructor(public view: string, public context: string, public discriminator: string) {}
}

export class ViewFlow extends ContainerControl {
    private renderedRowKeys: string[] = [];
    private renderedRows = Map<string, JQuery>();

    private views = new Subject<ViewBinding[]>();

    private readonly $container: JQuery;

    constructor(args: ControlArgs) {
        super(args);

        this.$element.addClass('control-container viewflow');

        this.$container = $('<div />')
            .addClass('viewflow-container')
            .addClass('viewflow-container-' + this.config.orientation.toLowerCase())
            .appendTo(this.$element);

        this.applyTooltip(this.$element);

        this.methods.register(this.config.elementName, this);

        this.subscriptions = disposableSubscription(
            this.processBindings(this.config),
            this.views.subscribe(v => this.updateElements(v)),
            this.values.bindOneWay('Rule', args.fieldContext, this.config.viewRule, x => {
                this.views.next(JSON.parse(x));
            })
        );

        this.initialize();
    }

    private updateElements(items: ViewBinding[]) {
        const makeKey = (item: ViewBinding) => `${item.view}|${item.context}|${item.discriminator}`;

        const keyedItems = items.map(x => new ViewBinding(x.view, x.context || '00000000-0000-0000-0000-000000000000', x.discriminator)).map(x => ({ key: makeKey(x), item: x }));

        const rowKeys = keyedItems.map(x => x.key);

        array.except(this.renderedRowKeys, ...rowKeys).forEach(x => {
            this.renderedRows.get(x).remove();
            this.renderedRows = this.renderedRows.remove(x);
        });

        const store = (k, v) => { this.renderedRows = this.renderedRows.set(k, v); return v; };

        preserveScroll(
            () => keyedItems.map(x => this.renderedRows.get(x.key) || store(x.key, this.buildElement(x.item.context, x.item.view))).forEach(x => this.$container.append(x)),
            this.$element
        );

        this.renderedRowKeys = rowKeys;
    }

    private buildElement(context: string, viewName: string) {
        const $elementContainer = $('<div />');

        this.eventAggregator.publish(new ViewRequested(viewName, view => {
            const $view = $(view);
            $elementContainer.empty().append($view);
            applyBindings($view, context, this.eventAggregator, new MethodRouter(this.methods, viewName), this.values, this.resources, this, this.serializer);

            const rootContainer = <RootContainer>$view.data('control');
            $elementContainer.height(rootContainer.configuredHeight()).width(rootContainer.configuredWidth());
            rootContainer.activate();

            this.reapplyEnabledState();
        }));

        return $elementContainer;
    }

    applyEnabledState(value: boolean) {
        this.propagateEnabledStateToChildElements(this.$container.children(), this.writableAgnosticDerivedEnabledState());
    }

    protected applyScrollVetoBehavior() { }

    focus() {
        this.setFocusToFirstFocusableSubElement(this.$container);
    }

    resizeStream(): Observable<{}> {
        return Observable.never();
    }
}

registerBinding('viewflow', ViewFlow);